import type { ReactNode, RefObject } from 'react';
import { ResultsChipSheet } from '../../shared/results_page/results_filter_chips.js';
import {
  RentalCategoryRows,
  RentalCompanyRows,
  RentalDailyPriceRange,
  type RentalCategoryOption,
  type RentalCompanyOption,
  type RentalFilterableOffer,
  type RentalResultsFiltersCopy,
  type RentalResultsFilterValue,
} from './rental_results_filters.js';

/** As folhas de valor dos chips — uma por eixo de corte que não é um toque só. */
export type RentalChipSheet = 'daily' | 'category' | 'company';

/**
 * As folhas PARCIAIS dos chips do carro (#5121, layout da #5125): Diária,
 * Categoria e Agência, cada uma com o MESMO corpo do painel completo sobre um
 * rascunho do filtro inteiro — Redefinir zera só o eixo da folha, Aplicar
 * escreve, fechar descarta. Com o rascunho do filtro inteiro, a contagem de
 * cada agência respeita os outros cortes como no painel.
 */
export function RentalChipSheets({
  sheet,
  onOpenChange,
  returnFocusTo,
  offers,
  currency,
  filters,
  copy,
  backLabel,
  applyLabel,
  locked,
  countsPartial,
  catalog,
  onApply,
}: {
  readonly sheet: RentalChipSheet | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  readonly offers: readonly RentalFilterableOffer[];
  readonly currency: string;
  readonly filters: RentalResultsFilterValue;
  readonly copy: RentalResultsFiltersCopy;
  readonly backLabel: string;
  /** O botão do pé das folhas ("Aplicar"). */
  readonly applyLabel: string;
  readonly locked: boolean;
  readonly countsPartial: boolean;
  /** As opções e a régua da diária, já resolvidas pelo catálogo da fila. */
  readonly catalog: {
    readonly priceFloor: number | undefined;
    readonly priceCeiling: number | undefined;
    readonly categories: readonly RentalCategoryOption[];
    readonly companies: readonly RentalCompanyOption[];
  };
  readonly onApply: (next: RentalResultsFilterValue) => void;
}): ReactNode {
  const { priceFloor, priceCeiling, categories, companies } = catalog;
  const shared = {
    onOpenChange,
    returnFocusTo,
    backLabel,
    resetLabel: copy.resetFiltersLabel,
    applyLabel,
    locked,
    value: filters,
    onApply,
  };
  const axis = (
    draft: RentalResultsFilterValue,
    setDraft: (next: RentalResultsFilterValue) => void,
  ) => ({ offers, value: draft, copy, locked, countsPartial, onChange: setDraft });
  return (
    <>
      {priceFloor !== undefined && priceCeiling !== undefined && (
        <ResultsChipSheet
          {...shared}
          open={sheet === 'daily'}
          title={copy.dailyPriceLabel}
          emptyValue={{ ...filters, minimumDailyPrice: undefined, maximumDailyPrice: undefined }}
        >
          {(draft, setDraft) => (
            <RentalDailyPriceRange
              floor={priceFloor}
              ceiling={priceCeiling}
              currency={currency}
              value={draft}
              copy={copy}
              locked={locked}
              onChange={setDraft}
            />
          )}
        </ResultsChipSheet>
      )}
      <ResultsChipSheet
        {...shared}
        open={sheet === 'category'}
        title={copy.categoryLabel}
        emptyValue={{ ...filters, categoryIds: [] }}
      >
        {(draft, setDraft) => (
          <RentalCategoryRows {...axis(draft, setDraft)} categories={categories} />
        )}
      </ResultsChipSheet>
      <ResultsChipSheet
        {...shared}
        open={sheet === 'company'}
        title={copy.companyLabel}
        emptyValue={{ ...filters, companyIds: [] }}
      >
        {(draft, setDraft) => (
          <RentalCompanyRows {...axis(draft, setDraft)} companies={companies} />
        )}
      </ResultsChipSheet>
    </>
  );
}
